import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Buildings, Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Eye, EyeSlash, Database } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
const KELOMPOK = ["Rawat Jalan", "Layanan Luar Klinik", "Unit Penunjang", "Kegawatdaruratan dan Rawat Inap"];
const empty = { nama: "", kode: "", kelompok: "Rawat Jalan", deskripsi: "", urutan: 0, aktif: true, lokasi: "GBS Clinic / Gerai", booking_publik: true, penunjang: false, ihs_location_id: "" };

export default function MasterUnits() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canManage = user?.role === "super_admin" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = () => api.get("/units").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, urutan: items.length }); setEditing("new"); };
  const openEdit = (u) => { setForm({ ...empty, ...u }); setEditing(u.id); };

  const save = async () => {
    if (!form.nama.trim() || !form.kode.trim()) return toast.error("Nama dan kode singkat unit wajib diisi");
    try {
      const payload = { ...form, nama: form.nama.trim(), kode: form.kode.trim().toUpperCase(), urutan: Number(form.urutan || 0), ihs_location_id: form.ihs_location_id || "" };
      if (editing === "new") await api.post("/units", payload);
      else await api.put(`/units/${editing}`, payload);
      toast.success("Unit disimpan"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/units/seed");
      toast.success(r.data.seeded ? `${r.data.seeded} unit bawaan GBS ditambahkan` : r.data.message);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSeeding(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/units/${confirmDel.id}`); toast.success("Unit dihapus"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.patch("/units/reorder", { ids: arr.map((u) => u.id) }); load(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  return (
    <div className="animate-fade-up space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Buildings size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Poli / Unit Pelayanan</h1></div>
          <p className="mt-1 text-sm text-slate-500">{items.length} unit · dipakai Master Layanan, kunjungan & laporan{!canManage && " · hanya lihat"}</p>
        </div>
        <div className="flex gap-2">
          {canManage && items.length === 0 && (
            <button data-testid="seed-units-button" disabled={seeding} onClick={doSeed} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 active:scale-95 disabled:opacity-60"><Database size={18} weight="bold" /> {seeding ? "Memproses..." : "Isi 11 Unit Bawaan GBS"}</button>
          )}
          {canManage && <button data-testid="add-unit-button" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7] active:scale-95"><Plus size={18} weight="bold" /> Tambah Unit</button>}
        </div>
      </div>

      {items.length === 0 && (
        <div data-testid="units-empty" className="rounded-2xl border border-dashed border-rose-200 bg-white p-8 text-center text-sm text-slate-500">
          Belum ada unit. Klik <b>Isi 11 Unit Bawaan GBS</b> untuk membuat daftar awal (aman, hanya jika kosong), atau tambah manual.
        </div>
      )}

      <div className="space-y-2">
        {items.map((u, idx) => (
          <div key={u.id} data-testid={`unit-${u.id}`} className="flex items-center gap-3 rounded-2xl border border-rose-100/70 bg-white p-3.5 shadow-sm">
            {canManage && (
              <div className="flex flex-col">
                <button data-testid={`unit-up-${u.id}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-0.5 text-slate-400 hover:text-[#6A38C7] disabled:opacity-30"><ArrowUp size={16} weight="bold" /></button>
                <button data-testid={`unit-down-${u.id}`} onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="rounded p-0.5 text-slate-400 hover:text-[#6A38C7] disabled:opacity-30"><ArrowDown size={16} weight="bold" /></button>
              </div>
            )}
            <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-lg bg-[#EEE6FF] text-xs font-bold text-[#6A38C7]">{u.kode}</span>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold text-slate-800">{u.nama}</p>
              <p className="truncate text-xs text-slate-400">{u.kelompok}{u.lokasi ? ` · ${u.lokasi}` : ""}{u.ihs_location_id ? ` · IHS: ${u.ihs_location_id}` : ""}</p>
            </div>
            {u.penunjang && <span className="hidden rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700 sm:inline-flex">Penunjang</span>}
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${u.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{u.aktif ? "Aktif" : "Nonaktif"}</span>
            <span className={`hidden items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold sm:inline-flex ${u.booking_publik ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-400"}`}>{u.booking_publik ? <Eye size={12} /> : <EyeSlash size={12} />} {u.booking_publik ? "Publik" : "Internal"}</span>
            {canManage && (
              <div className="flex gap-1.5">
                <button data-testid={`edit-unit-${u.id}`} onClick={() => openEdit(u)} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600"><PencilSimple size={14} /> Edit</button>
                {isSuper && <button data-testid={`delete-unit-${u.id}`} onClick={() => setConfirmDel(u)} className="flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-600"><Trash size={14} /></button>}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="unit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "Tambah Unit" : "Edit Unit"}</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Nama Unit</label><input data-testid="unit-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Kode Singkat</label><input data-testid="unit-kode" value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} className={inputCls} placeholder="mis. KIA" /></div>
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Urutan Tampil</label><input data-testid="unit-urutan" type="number" value={form.urutan} onChange={(e) => setForm({ ...form, urutan: e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Kelompok</label>
              <select data-testid="unit-kelompok" value={form.kelompok} onChange={(e) => setForm({ ...form, kelompok: e.target.value })} className={inputCls}>
                {KELOMPOK.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Lokasi Pelayanan</label><input data-testid="unit-lokasi" value={form.lokasi || ""} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} className={inputCls} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">IHS Location ID (SATUSEHAT) <span className="font-normal text-slate-400">— disiapkan, belum dikirim</span></label><input data-testid="unit-ihs" value={form.ihs_location_id || ""} onChange={(e) => setForm({ ...form, ihs_location_id: e.target.value })} className={inputCls} placeholder="kosongkan bila belum ada" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="unit-aktif" type="checkbox" checked={!!form.aktif} onChange={(e) => setForm({ ...form, aktif: e.target.checked })} /> Aktif</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="unit-publik" type="checkbox" checked={!!form.booking_publik} onChange={(e) => setForm({ ...form, booking_publik: e.target.checked })} /> Tampil di booking publik</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="unit-penunjang" type="checkbox" checked={!!form.penunjang} onChange={(e) => setForm({ ...form, penunjang: e.target.checked })} /> Unit penunjang (Lab/Farmasi)</label>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="save-unit" onClick={save} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white hover:bg-[#6A38C7]">Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="confirm-delete-unit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Hapus Unit?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Yakin ingin menghapus unit <b>{confirmDel?.nama}</b>? Unit yang masih dipakai layanan tidak dapat dihapus — nonaktifkan saja.</p>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="confirm-delete-unit" onClick={doDelete} className="rounded-xl bg-rose-500 px-5 py-2 text-sm font-bold text-white hover:bg-rose-600">Hapus</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
